import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Background_Clip = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">✂️ Advanced CSS: background-clip</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>background-clip</code> property controls{" "}
        <b>how far the background (color or image)</b> extends inside the box
        model: across the <b>border</b>, <b>padding</b>, or just the{" "}
        <b>content</b>.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fbcfe8, #f9a8d4)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#9d174d" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>border-box</code>: background is painted under the border.
          </li>
          <li>
            <code>padding-box</code>: background only inside the padding and
            content, not under the border.
          </li>
          <li>
            <code>content-box</code>: background appears only behind the content
            area.
          </li>
          <li>
            <code>text</code>: special value (with{" "}
            <code>-webkit-background-clip</code>) clips background to text
            glyphs (great for gradient text).
          </li>
          <li>
            Often combined with <code>background-origin</code> for fine control.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Default is border-box */
background-clip: border-box | padding-box | content-box | text;`}
        />
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">⚡ Advanced Examples</h2>

        {/* Example 1 */}
        <h3 className="section-subtitle">1. border-box (default)</h3>
        <CodeCopyBlock
          code={`div {
  background: url("pattern.png");
  background-clip: border-box;
  border: 10px dashed #f87171;
}`}
        />
        <div
          style={{
            width: "280px",
            height: "120px",
            border: "10px dashed #f87171",
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/cubes.png')",
            backgroundSize: "60px 60px",
            backgroundClip: "border-box",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 2 */}
        <h3 className="section-subtitle">2. padding-box (inside border)</h3>
        <CodeCopyBlock
          code={`div {
  background: url("pattern.png");
  background-clip: padding-box;
  border: 10px solid #60a5fa;
}`}
        />
        <div
          style={{
            width: "280px",
            height: "120px",
            border: "10px solid #60a5fa",
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/cubes.png')",
            backgroundSize: "60px 60px",
            backgroundClip: "padding-box",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 3 */}
        <h3 className="section-subtitle">3. content-box (only content area)</h3>
        <CodeCopyBlock
          code={`div {
  background: url("pattern.png");
  background-clip: content-box;
  border: 10px solid #34d399;
  padding: 20px;
}`}
        />
        <div
          style={{
            width: "280px",
            height: "120px",
            border: "10px solid #34d399",
            padding: "20px",
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/cubes.png')",
            backgroundSize: "60px 60px",
            backgroundClip: "content-box",
            marginTop: "0.5rem",
          }}
        >
          <p style={{ textAlign: "center", margin: 0, fontWeight: "bold" }}>
            Content Box
          </p>
        </div>

        {/* Example 4 */}
        <h3 className="section-subtitle">4. Gradient Text with text clip</h3>
        <CodeCopyBlock
          code={`.text-gradient {
  font-size: 2rem;
  font-weight: bold;
  background: linear-gradient(90deg, #ec4899, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text; /* works in some modern browsers */
}`}
        />
        <div
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            background: "linear-gradient(90deg, #ec4899, #8b5cf6)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            marginTop: "0.5rem",
          }}
        >
          Gradient Text
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you’ve learned <code>background-clip</code>, try combining it
          with <code>background-origin</code> and <code>background-size</code>{" "}
          for powerful visual effects.
        </p>
      </div>
    </div>
  );
};

export default Background_Clip;
